import { useRef } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'

const LENGTH = 6

/**
 * `div.otp` → `otp-input` (process/08-COMPONENT-CATALOGUE.md). "The segmented one-time-code
 * input." gap 8px → --sp-4 (0 delta); margin-bottom 16px → --sp-8 (0 delta); each input 44×48px
 * is out of the token map's scope (width/height); text weight 600 → --fw-semibold, size 19px →
 * --fs-section (0 delta); border-radius 3px → --r-1 (0 delta).
 *
 * `line-height: 1` on the prototype's `.otp input` is a value process/11-TOKEN-MAP.md's own
 * denominator did not carry a row for — it is not in the `leading` family's four steps and not
 * in the "not a token" table either. Left unset (the browser default) rather than folding it to
 * the nearest `--lh-*` step on this pass's own authority; reported as a finding, not decided here.
 */
export function OtpInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const refs = useRef<(HTMLInputElement | null)[]>([])

  function setDigit(index: number, digit: string) {
    const next = value.padEnd(LENGTH, ' ').split('')
    next[index] = digit || ' '
    onChange(next.join('').trimEnd())
  }

  function handleChange(index: number, e: ChangeEvent<HTMLInputElement>) {
    const digit = e.target.value.replace(/[^0-9]/g, '').slice(-1)
    setDigit(index, digit)
    if (digit && index < LENGTH - 1) {
      refs.current[index + 1]?.focus()
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  return (
    <div data-cmp="otp-input" className="flex gap-4 mb-8">
      {Array.from({ length: LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index]?.trim() ?? ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          aria-label={`Confirmation code digit ${index + 1} of ${LENGTH}`}
          style={{ width: '44px', height: '48px' }}
          className="text-center font-semibold text-section text-tx border border-bd2 rounded-1 font-mono focus:border-blue focus:shadow-focus focus:outline-none"
        />
      ))}
    </div>
  )
}
